/** Neutral portrait stage shared by the app and the offline roster shooter.
 * Fresh exported GLB bytes isolate capture from playback and existing favourites.
 * Owns its renderer and loaded assets; missing anatomy refuses visibly. */
export interface PortraitAdjustment { readonly zoom?:number; readonly right?:number; readonly up?:number; readonly centerOnHead?:boolean; }
import * as THREE from 'three';
import { HERO_SIZE } from '../package/hero-size.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

export function createPortraitStage() {
const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
renderer.setSize(HERO_SIZE, HERO_SIZE);
renderer.setPixelRatio(1);
renderer.toneMapping = THREE.NeutralToneMapping;
renderer.setClearColor(0, 0);

const pmrem = new THREE.PMREMGenerator(renderer);
const room = new RoomEnvironment();
const environment = pmrem.fromScene(room, 0.04);
room.dispose();
pmrem.dispose();

const capture = async (bytes: ArrayBuffer, adjustment: PortraitAdjustment = {}) => {
  const gltf = await new GLTFLoader().parseAsync(bytes, '');
  const root = gltf.scene;
  const scene = new THREE.Scene();
  scene.environment = environment.texture;
  scene.environmentIntensity = 0.45;
  scene.add(root);
  try {
    // Deliberately never play embedded animation: every subject starts at rest.
    root.updateMatrixWorld(true);
    const bones: THREE.Bone[] = [];
    root.traverse(n => { if (n instanceof THREE.Bone) bones.push(n); });
    const head = ['Head', 'J_Bip_C_Head', 'mixamorigHead', 'mixamorig:Head', 'head']
      .map(name => bones.find(b => b.name === name)).find(Boolean)
      ?? bones.find(b => /(^|[:_.])head$/i.test(b.name));
    if (!head) throw new Error('No head bone; manual framing needed');
    const headBones = new Set<THREE.Bone>();
    head.traverse(n => { if (n instanceof THREE.Bone) headBones.add(n); });
    let points: THREE.Vector3[] = [];
    root.traverse(mesh => {
      if (!(mesh instanceof THREE.SkinnedMesh)) return;
      const positions = mesh.geometry.getAttribute('position');
      const indices = mesh.geometry.getAttribute('skinIndex');
      const weights = mesh.geometry.getAttribute('skinWeight');
      if (!indices || !weights) return;
      mesh.skeleton.update();
      for (let i = 0; i < positions.count; i++) {
        let weight = 0;
        for (let j = 0; j < 4; j++) if (headBones.has(mesh.skeleton.bones[indices.getComponent(i, j)]!)) weight += weights.getComponent(i, j);
        if (weight < 0.5) continue;
        const p = new THREE.Vector3();
        mesh.getVertexPosition(i, p);
        points.push(p.applyMatrix4(mesh.matrixWorld));
      }
    });
    if (points.length < 32) throw new Error('Too little head-weighted geometry; manual framing needed');
    // Feet-to-toes supplies facing independently of the camera and file origin.
    const front = new THREE.Vector3();
    let toePairs = 0;
    for (const side of ['L', 'R']) {
      const word = side === 'L' ? 'Left' : 'Right';
      const footNames = [`${side}_Foot`, `J_Bip_${side}_Foot`, `${word}Foot`, `mixamorig${word}Foot`, `mixamorig:${word}Foot`];
      const toeNames = [`${side}_ToeBase`, `J_Bip_${side}_ToeBase`, `${word}ToeBase`, `mixamorig${word}ToeBase`, `mixamorig:${word}ToeBase`];
      const foot = bones.find(b => footNames.includes(b.name));
      const toe = bones.find(b => toeNames.includes(b.name));
      if (foot && toe) { front.add(toe.getWorldPosition(new THREE.Vector3()).sub(foot.getWorldPosition(new THREE.Vector3()))); toePairs++; }
    }
    if (toePairs !== 2 || Math.hypot(front.x, front.z) < 1e-6) throw new Error('Cannot establish facing from both feet; manual framing needed');
    front.y = 0;
    // Use the dominant cardinal axis, matching Harry's rig-facing vocabulary.
    if (Math.abs(front.z) >= Math.abs(front.x)) front.set(0, 0, Math.sign(front.z));
    else front.set(Math.sign(front.x), 0, 0);
    const right = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), front);
    // Long hair and beards can be fully head-weighted down to the chest.
    // Use the head joint as the lower anatomical anchor, not the hair tips.
    const headPosition = head.getWorldPosition(new THREE.Vector3());
    const crown = points.reduce((top, p) => Math.max(top, p.y), -Infinity);
    const jointToCrown = crown - headPosition.y;
    if (!(jointToCrown > 0)) throw new Error('Head crown is below its joint; manual framing needed');
    const lowerHead = headPosition.y - jointToCrown * 0.20;
    points = points.filter(p => p.y >= lowerHead);
    const bounds = new THREE.Box3().setFromPoints(points);
    const height = bounds.max.y - bounds.min.y;
    if (!(height > 0)) throw new Error('Head has no measurable height');
    const target = bounds.getCenter(new THREE.Vector3());
    target.y -= height * 0.10; // consistent room for a little shoulder below the face
    const tan = Math.tan(0.42 / 2);
    let distance = 0;
    for (const p of points) {
      const rel = p.clone().sub(target);
      distance = Math.max(distance, rel.dot(front) + Math.max(Math.abs(rel.dot(right)) / (tan * 0.84), Math.abs(rel.y) / (tan * 0.84)));
    }
    // Field corrections are relative to head height, never absolute rig units.
    // Positive right/up moves the portrait that way on the image.
    if (adjustment.centerOnHead) target.addScaledVector(right, headPosition.clone().sub(target).dot(right));
    target.addScaledVector(right, -(adjustment.right ?? 0) * height);
    target.y -= (adjustment.up ?? 0) * height;
    distance /= adjustment.zoom ?? 1;
    const camera = new THREE.PerspectiveCamera(0.42 * 180 / Math.PI, 1, distance / 100, distance * 100);
    camera.position.copy(target).addScaledVector(front, distance);
    camera.lookAt(target);
    const key = new THREE.DirectionalLight(0xffffff, 2.6);
    key.position.copy(target).addScaledVector(front, distance * 2).addScaledVector(right, -distance * 0.93);
    key.position.y += distance * 1.87;
    key.target.position.copy(target);
    scene.add(key, key.target);
    renderer.render(scene, camera);
    const gl = renderer.getContext();
    if (gl.isContextLost()) throw new Error('Rendering context lost');
    return {
      png: Uint8Array.from(atob(renderer.domElement.toDataURL('image/png').split(',')[1]!), c => c.charCodeAt(0)),
      headBone: head.name, headVertices: points.length, headHeight: height,
      facing: front.toArray(), distance, target: target.toArray(), adjustment,
    };
  } finally {
    const textures = new Set<THREE.Texture>();
    const materials = new Set<THREE.Material>();
    root.traverse(n => {
      if (!(n instanceof THREE.Mesh)) return;
      n.geometry.dispose();
      if (n instanceof THREE.SkinnedMesh) n.skeleton.dispose();
      for (const m of Array.isArray(n.material) ? n.material : [n.material]) {
        materials.add(m);
        for (const value of Object.values(m)) if (value instanceof THREE.Texture) textures.add(value);
      }
    });
    for (const texture of textures) { if (typeof ImageBitmap !== 'undefined' && texture.source.data instanceof ImageBitmap) texture.source.data.close(); texture.dispose(); }
    for (const material of materials) material.dispose();
    renderer.renderLists.dispose();
  }
};

return { capture, dispose() { environment.dispose(); renderer.dispose(); } };
}
